import React, { useRef, useEffect, useMemo } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import Header from "./Header";
import Footer from "./Footer";

// ── Three.js particle cloud ──────────────────────────────────────────────────

function ParticleCloud() {
  const points = useRef<THREE.Points>(null);
  const linesRef = useRef<THREE.LineSegments>(null);

  const { particlesGeometry, connectionsGeometry } = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const count = 400;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count * 3; i += 3) {
      positions[i]     = (Math.random() - 0.5) * 30;
      positions[i + 1] = (Math.random() - 0.5) * 30;
      positions[i + 2] = (Math.random() - 0.5) * 30;
    }
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const connGeo = new THREE.BufferGeometry();
    const connPos = new Float32Array(count * 3 * 2);
    let ci = 0;
    for (let i = 0; i < count; i++) {
      const x1 = positions[i * 3], y1 = positions[i * 3 + 1], z1 = positions[i * 3 + 2];
      for (let j = i + 1; j < count; j++) {
        const x2 = positions[j * 3], y2 = positions[j * 3 + 1], z2 = positions[j * 3 + 2];
        const d = Math.sqrt((x2-x1)**2 + (y2-y1)**2 + (z2-z1)**2);
        if (d < 5 && Math.random() > 0.97 && ci < count * 2) {
          connPos[ci*3]=x1; connPos[ci*3+1]=y1; connPos[ci*3+2]=z1;
          connPos[ci*3+3]=x2; connPos[ci*3+4]=y2; connPos[ci*3+5]=z2;
          ci += 2;
        }
      }
    }
    connGeo.setAttribute("position", new THREE.BufferAttribute(connPos.slice(0, ci * 3), 3));
    return { particlesGeometry: geometry, connectionsGeometry: connGeo };
  }, []);

  const particleMat = useMemo(() => new THREE.PointsMaterial({
    color: "#92D7FF", size: 0.05, sizeAttenuation: true, transparent: true, opacity: 0.7,
  }), []);

  const lineMat = useMemo(() => new THREE.LineBasicMaterial({
    color: "#8EC9FF", transparent: true, opacity: 0.5,
  }), []);

  useEffect(() => {
    let raf: number;
    const animate = () => {
      lineMat.opacity = 0.4 + Math.sin(Date.now() * 0.0015) * 0.15;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [lineMat]);

  return (
    <>
      <points ref={points} geometry={particlesGeometry} material={particleMat} />
      <lineSegments ref={linesRef} geometry={connectionsGeometry} material={lineMat} />
    </>
  );
}

function DatathonThreeScene() {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
      <Canvas camera={{ position: [0, 5, 10], fov: 50 }} style={{ background: "transparent" }}>
        <ambientLight intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={0.5} color="#8EC9FF" />
        <pointLight position={[-10, -10, -10]} intensity={0.3} color="#92D7FF" />
        <ParticleCloud />
        <OrbitControls
          enableZoom={false} enablePan={false}
          autoRotate autoRotateSpeed={0.5}
          maxPolarAngle={Math.PI / 2} minPolarAngle={Math.PI / 2}
        />
      </Canvas>
    </div>
  );
}

// ── Framer Motion variants ───────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.3 } },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: "spring" as const, stiffness: 100, damping: 10 } },
};

// ── Main component ───────────────────────────────────────────────────────────

const Datathon: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);

  const { scrollY } = useScroll();
  const scrollX = useTransform(scrollY, [0, 400], [0, -200]);
  const xSpring = useSpring(scrollX, { stiffness: 100, damping: 30 });

  // Canvas line animation (ported from datafest HeroSection)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const DPR = Math.max(window.devicePixelRatio || 1, 1);

    const resize = () => {
      const w = heroRef.current?.clientWidth  || window.innerWidth;
      const h = heroRef.current?.clientHeight || window.innerHeight;
      canvas.width  = w * DPR;
      canvas.height = h * DPR;
      canvas.style.width  = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const NUM_LINES = 12;
    const bendDirections = [1,-1,-1,1,1,-1,-1,1,1,-1,-1,1];
    const yPositions    = [0.2,0.28,0.35,0.45,0.52,0.58,0.65,0.72,0.78,0.85,0.45,0.58];
    const verticalLens  = [90,60,50,75,85,45,70,55,65,80,40,95];

    const lines = Array.from({ length: NUM_LINES }, (_, i) => {
      const fromLeft = i % 2 === 0;
      const w = canvas.width / DPR;
      const h = canvas.height / DPR;
      const startY = h * yPositions[i];
      const hLen = i % 3 === 0 ? w * 0.32 : i % 3 === 1 ? w * 0.2 : w * 0.12;
      return { fromLeft, startY, bendYDir: bendDirections[i], horizontalLen: hLen, verticalLen: verticalLens[i], progress: 0, delay: i * 250, lineWidth: 2 };
    });

    const duration  = 2000;
    const startTime = performance.now();

    const draw = (now: number) => {
      const w = canvas.width / DPR;
      const h = canvas.height / DPR;
      const elapsed = now - startTime;
      ctx.clearRect(0, 0, w, h);

      if (window.innerWidth >= 1024) {
        lines.forEach((line) => {
          const t = Math.min(Math.max(0, elapsed - line.delay) / duration, 1);
          line.progress = t;

          const startX = line.fromLeft ? 0 : w;
          const bendX  = line.fromLeft
            ? Math.min(line.horizontalLen, w * 0.7)
            : Math.max(w - line.horizontalLen, w * 0.3);
          const endY = line.startY + line.verticalLen * line.bendYDir;
          const pathLen = line.horizontalLen + line.verticalLen;
          const cur = pathLen * t;

          ctx.beginPath();
          if (cur <= line.horizontalLen) {
            const cx = line.fromLeft ? startX + cur : startX - cur;
            ctx.moveTo(startX, line.startY);
            ctx.lineTo(cx, line.startY);
          } else {
            const cy = line.startY + (cur - line.horizontalLen) * line.bendYDir;
            ctx.moveTo(startX, line.startY);
            ctx.lineTo(bendX, line.startY);
            ctx.lineTo(bendX, cy);
          }

          const alpha = 0.5 + 0.15 * Math.sin(now / 800);
          const grad = ctx.createLinearGradient(startX, line.startY, bendX, endY);
          grad.addColorStop(0,   `rgba(142,201,255,${alpha})`);
          grad.addColorStop(0.5, `rgba(146,215,255,${alpha + 0.2})`);
          grad.addColorStop(1,   `rgba(142,201,255,${alpha})`);

          ctx.strokeStyle = grad;
          ctx.lineWidth   = line.lineWidth;
          ctx.shadowBlur  = 0;
          ctx.lineJoin    = "round";
          ctx.lineCap     = "round";
          ctx.stroke();

          if (t >= 1) {
            const cube = 5 + Math.abs(Math.sin(now / 500)) * 2;
            ctx.fillStyle  = `rgba(146,215,255,${0.8 + 0.15 * Math.sin(now / 400)})`;
            ctx.shadowBlur = 0;
            ctx.fillRect(bendX - cube/2, endY - cube/2, cube, cube);
          }
        });
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-[#F4F1EC]">
      <Header />

      {/* ── Hero ── */}
      <motion.section
        ref={heroRef}
        className="relative w-full min-h-[100vh] h-screen overflow-hidden bg-[#F4F1EC]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Three.js particles */}
        <DatathonThreeScene />

        {/* Canvas lines */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
          <canvas ref={canvasRef} className="block w-full h-full" />
        </div>

        {/* Floating card — same as SlowisFast */}
        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 20 }}>
          <motion.div
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/data.png`}
              alt="Sheridan Datathon"
              className="w-72 h-auto"
              whileHover={{ scale: 1.1, transition: { duration: 0.3, ease: "easeOut" } }}
            />
          </motion.div>
        </div>

        {/* Scrolling title — same position/size as SlowisFast, white for dark bg */}
        <motion.h1
          className="relative z-10 text-[28vw] sm:text-[25vw] md:text-[20vw] font-serif font-bold text-right mt-[60vh] md:mt-[45vh] pr-[5vw] whitespace-nowrap text-black"
          style={{ x: xSpring }}
        >
          DATATHON
        </motion.h1>

        {/* Bottom fade to page background */}
        <div className="absolute bottom-0 left-0 right-0 h-[30vh] bg-gradient-to-t from-[#F4F1EC] to-transparent z-40" />
      </motion.section>

      {/* ── Meta ── */}
      <motion.section
        className="w-full bg-[#F4F1EC] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-12 border-b border-gray-300 pb-10">
            {[
              { label: "Role",  value: "Web Designer · Frontend Developer" },
              { label: "Type",  value: "Landing Page" },
              { label: "Focus", value: "Web Design · Web Development" },
            ].map((m) => (
              <div key={m.label}>
                <p className="text-xs uppercase tracking-widest mb-1 font-sans text-gray-500">{m.label}</p>
                <p className="text-black font-medium font-sans">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ── Overview ── */}
      <motion.section
        className="w-full bg-[#F4F1EC] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Overview</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold mb-6">Project Overview</h2>
            <div className="space-y-5 text-lg leading-relaxed text-gray-700 font-sans">
              <p>
                The Datathon Landing Page is a fully designed and developed promotional website for a
                data science competition. The goal was to create an engaging, informative entry point
                that captures excitement for the event while clearly communicating schedules, prizes,
                and registration details.
              </p>
            </div>
            <ul className="mt-8 space-y-3">
              {[
                "Bold hero section that immediately communicates the event's scale and energy",
                "Responsive layout optimized for desktop and mobile participants",
                "Clear information hierarchy for schedule, sponsors, and prizes",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-lg text-gray-700 font-sans">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4F8EF7] mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative w-full aspect-video rounded-2xl bg-[#0D0D1A] flex items-center justify-center overflow-hidden shadow-xl">
            <img
              src={`${process.env.PUBLIC_URL}/datathon-preview.png`}
              alt="Datathon Landing Page Preview"
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <p className="text-gray-500 font-sans text-sm absolute">Preview Image</p>
          </div>
        </div>
      </motion.section>

      {/* ── Challenge ── */}
      <motion.section
        className="w-full bg-[#0D0D1A] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-4">Challenge</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white mb-8">
            Excitement vs. Clarity
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div className="space-y-5 text-lg leading-relaxed text-gray-300 font-sans">
              <p>
                Datathon landing pages need to do two conflicting things at once: generate hype and
                deliver dense information. Too much energy and the schedule gets lost. Too structured
                and the page feels like a spreadsheet.
              </p>
              <p>
                The challenge was building a visual hierarchy that lets visitors feel the energy first,
                then naturally find the details they need — all in a single scroll.
              </p>
            </div>
            <div className="space-y-6">
              {[
                { n: "01", body: "Balancing visual impact with information density across the page" },
                { n: "02", body: "Ensuring full responsiveness for participants on mobile devices" },
                { n: "03", body: "Creating a registration flow that converts visitors to sign-ups" },
              ].map((item) => (
                <div key={item.n} className="flex gap-6 items-start">
                  <span className="text-[#4F8EF7] font-serif font-bold text-2xl flex-shrink-0">{item.n}</span>
                  <p className="text-gray-300 font-sans text-lg leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── Key Features ── */}
      <motion.section
        className="w-full bg-[#F4F1EC] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Features</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold mb-10">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", title: "Hero with Impact",   body: "A full-viewport hero with 3D particle scene, animated canvas lines, and a glowing CTA — designed to hook visitors in seconds." },
              { n: "02", title: "Event Timeline",     body: "A scannable schedule section that communicates deadlines, check-ins, and submission windows without overwhelming the reader." },
              { n: "03", title: "Fully Responsive",   body: "Every section adapts seamlessly from a 1440px desktop to a 375px mobile screen, keeping energy and readability intact." },
            ].map((item) => (
              <div key={item.n} className="border border-black p-8">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-6">{item.n}</p>
                <h4 className="text-2xl font-serif mb-4">{item.title}</h4>
                <p className="text-gray-600 font-sans leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ── Color & Type ── */}
      <motion.section
        className="w-full bg-[#F4F1EC] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="w-40 sm:w-60 md:w-80 h-[2px] bg-[#4F8EF7] mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {[
                { hex: "#0D0D1A", label: "#0D0D1A", dark: true  },
                { hex: "#92D7FF", label: "#92D7FF", dark: false },
                { hex: "#F4F1EC", label: "#F4F1EC", dark: false },
                { hex: "#1E2A4A", label: "#1E2A4A", dark: true  },
              ].map((c) => (
                <div
                  key={c.hex}
                  className="w-full h-32 sm:h-36 md:h-40 relative rounded-[16px] border border-black/10"
                  style={{ backgroundColor: c.hex }}
                >
                  <span className={`absolute bottom-2 left-3 text-xs font-mono ${c.dark ? "text-white" : "text-black"}`}>
                    {c.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-col justify-center gap-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-2">Display</p>
                <p className="font-serif font-bold leading-tight" style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}>
                  Aa Bb Cc Dd Ee Ff
                </p>
                <p className="text-gray-500 font-sans text-sm mt-1">Serif — headings &amp; hero</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-2">Body</p>
                <p className="font-mono leading-relaxed text-gray-700" style={{ fontSize: "clamp(1rem, 2vw, 1.5rem)" }}>
                  Aa Bb Cc Dd Ee Ff Gg Hh Ii
                </p>
                <p className="text-gray-500 font-sans text-sm mt-1">Monospace — labels &amp; code</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── Reflection ── */}
      <motion.section
        className="w-full bg-[#0D0D1A] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-4">Reflection</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-white leading-snug">
              A great landing page doesn't just inform — it makes you want to show up.
            </h2>
            <div className="space-y-5 text-gray-300 font-sans text-lg leading-relaxed">
              <p>
                This project pushed me to think about the full visitor journey: what do they need to
                feel first, and what do they need to know next?
              </p>
              <p>
                Getting both the design and the code right — ensuring the layout held up at every
                breakpoint — made this one of the most complete end-to-end executions in my portfolio.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      <div className="flex justify-center mt-12 sm:mt-16 md:mt-20 mb-20 sm:mb-32 md:mb-40">
        <Link
          to="/all-projects"
          onClick={() => window.scrollTo(0, 0)}
          className="bg-[#92D7FF]/20 text-[#92D7FF] border-2 border-[#92D7FF] px-12 sm:px-16 md:px-20 py-3 sm:py-4 font-mono text-sm sm:text-base font-bold transition-all duration-300 ease-in-out hover:-translate-y-1 hover:bg-[#92D7FF] hover:text-black hover:shadow-[0_0_18px_rgba(146,215,255,0.85)] active:translate-y-0 rounded-[23px]"
        >
          VIEW ALL PROJECTS
        </Link>
      </div>

      <Footer />
    </div>
  );
};

export default Datathon;
